import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { chatSessionForCheckout } from "@/lib/chatAttribution";
import { Minus, Plus, Trash2 } from "lucide-react";
import { api, money, errMsg, imgUrl } from "@/lib/api";
import { track } from "@/lib/track";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { CART } from "@/constants/testIds";
import { BundleNudge } from "@/components/BundleNudge";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import { useInView } from "@/lib/useInView";
import { isStandalone } from "@/lib/appTracking";

const Row = ({ label, value, testId, accent }) => (
  <div className={`flex justify-between ${accent ? "text-[#4E7B68]" : ""}`}>
    <span>{label}</span>
    <span data-testid={testId}>{value}</span>
  </div>
);

export default function CartPage() {
  const { items, add, setQty, code, setCode, restore } = useCart();
  const [params, setParams] = useSearchParams();
  const restoredRef = useRef(false);
  const { user } = useAuth();
  const [codeInput, setCodeInput] = useState(code);
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [checkoutRef, checkoutInView] = useInView();
  const payload = items.map(({ slug, qty }) => ({ slug, qty }));

  useEffect(() => {
    const saved = params.get("restore");
    if (!saved || restoredRef.current) return;
    restoredRef.current = true;
    const wanted = Object.fromEntries(saved.split(",").map((s) => s.split(":")).filter(([slug, n]) => slug && Number(n) > 0).map(([slug, n]) => [slug, Math.min(50, Math.floor(Number(n)))]));
    setParams({}, { replace: true });
    if (!Object.keys(wanted).length) return;
    api.get("/products")
      .then(({ data }) => {
        const found = data.filter((p) => wanted[p.slug]).map((p) => ({ ...p, qty: wanted[p.slug] }));
        if (found.length) { restore(found); toast.success("Your saved cart is back"); }
        if (found.length < Object.keys(wanted).length) toast.error(found.length ? "A few items from your saved cart are no longer available" : "The items in your saved cart are no longer available");
      })
      .catch(() => toast.error("Couldn't load your saved cart. Please try again"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!items.length) return setQuote(null);
    api
      .post("/cart/quote", { items: payload, discount_code: code || null })
      .then(({ data }) => setQuote(data))
      .catch((e) => { setError(errMsg(e)); if (code) setCode(""); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(payload), code]);

  const applyCode = async (e) => {
    e.preventDefault();
    const next = codeInput.trim().toUpperCase();
    try {
      await api.post("/cart/quote", { items: payload, discount_code: next || null });
      setError("");
      setCode(next);
    } catch (err) {
      setError(errMsg(err));
    }
  };

  const checkout = async () => {
    setBusy(true);
    track("begin_checkout", { value: quote?.total ?? null, qty: items.reduce((n, i) => n + i.qty, 0), slugs: items.map((i) => i.slug) });
    try {
      const { data } = await api.post("/checkout", { items: payload, discount_code: code || null, origin_url: window.location.origin, email: user ? null : email || null, ref: localStorage.getItem("pettok_ref"), chat_session: chatSessionForCheckout(), from_app: isStandalone() });
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(errMsg(e));
      setBusy(false);
    }
  };

  if (!items.length)
    return (
      <main data-testid={CART.empty} className="pt-32 pb-10 max-w-3xl mx-auto px-6 text-center min-h-[60vh]">
        <h1 className="font-heading text-4xl font-extrabold">Your cart is empty.</h1>
        <p className="mt-3 text-stone-600">Kiddee's picks are a good place to start.</p>
        <Link to="/shop" className="mt-8 inline-block bg-[#1C1917] text-white px-7 py-3.5 rounded-full hover:bg-[#E06D44] transition-colors">Go to the shop</Link>
      </main>
    );

  return (
    <main data-testid={CART.page} className="pt-24 sm:pt-28 pb-28 md:pb-10 max-w-6xl mx-auto px-5 sm:px-6 lg:px-10">
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Your cart</h1>
      <div className="mt-6 sm:mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
        <ul className="lg:col-span-7 divide-y divide-[#F0EBE1]">
          {items.map((i) => (
            <li key={i.slug} data-testid={CART.line(i.slug)} className="py-5 flex gap-3 sm:gap-4 items-start sm:items-center">
              <img src={imgUrl(i.image)} alt={i.name} className="w-20 h-20 rounded-2xl object-cover bg-[#F0EBE1] shrink-0" />
              <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${i.slug}`} className="font-heading font-bold leading-snug hover:text-[#E06D44] line-clamp-2">{i.name}</Link>
                  <p className="text-sm text-stone-500 mt-1">{money(i.price)}</p>
                </div>
                <div className="flex items-center justify-between sm:justify-start gap-2">
                  <div className="flex items-center border border-[#E7E0D3] rounded-full">
                    <button data-testid={CART.minus(i.slug)} aria-label="Less" onClick={() => setQty(i.slug, i.qty - 1)} className="w-11 h-11 grid place-items-center"><Minus size={14} /></button>
                    <span className="w-6 text-center text-sm">{i.qty}</span>
                    <button data-testid={CART.plus(i.slug)} aria-label="More" onClick={() => setQty(i.slug, i.qty + 1)} className="w-11 h-11 grid place-items-center"><Plus size={14} /></button>
                  </div>
                  <button data-testid={CART.remove(i.slug)} aria-label="Remove" onClick={() => setQty(i.slug, 0)} className="w-11 h-11 grid place-items-center rounded-full text-stone-400 hover:text-[#E11D48] hover:bg-[#FDF2EC] transition-colors"><Trash2 size={18} /></button>
                </div>
              </div>
            </li>
          ))}
        </ul>
        <aside className="lg:col-span-5 bg-white border border-[#F0EBE1] rounded-[1.75rem] p-6 space-y-5">
          <form className="flex gap-2" onSubmit={applyCode}>
            <input data-testid={CART.codeInput} value={codeInput} onChange={(e) => setCodeInput(e.target.value)} placeholder="Discount code" className="flex-1 min-w-0 px-4 py-2.5 rounded-full border border-[#E7E0D3] uppercase placeholder:normal-case focus:outline-none focus:ring-2 focus:ring-[#E06D44]/40" />
            <button data-testid={CART.codeApply} className="px-5 rounded-full border border-[#1C1917] text-sm font-medium hover:bg-[#1C1917] hover:text-white transition-colors">Apply</button>
          </form>
          {error && <p data-testid={CART.codeError} className="text-sm text-[#E11D48]">{error}</p>}
          {quote?.bundle_nudges?.map((n) => <BundleNudge key={n.bundle} n={n} onAdd={() => add(n.product, 1, { source: "bundle_nudge" })} />)}
          {quote && (
            <div className="space-y-2 text-sm">
              <Row label="Subtotal" value={money(quote.subtotal)} testId={CART.subtotal} />
              {quote.discount > 0 && !quote.bundles?.length && <Row label={`Discount (${quote.discount_code})`} value={`−${money(quote.discount)}`} testId={CART.discount} accent />}
              {quote.bundles?.length > 0 && <Row label={`Bundle savings (${quote.bundles.map((b) => (b.sets > 1 ? `${b.name} ×${b.sets}` : b.name)).join(", ")})`} value={`−${money(quote.discount)}`} testId={CART.bundleSavings} accent />}
              {quote.code_skipped && <p data-testid={CART.codeSkipped} className="text-xs text-[#4E7B68]">Your bundle saves more than {quote.code_skipped}, so we applied the bundle price. Codes and bundles don't combine.</p>}
              <Row label="Shipping" value={quote.shipping ? money(quote.shipping) : "Free"} testId={CART.shipping} />
              <div className="pt-3 border-t border-[#F0EBE1] flex justify-between font-heading text-lg font-bold">
                <span>Total</span><span data-testid={CART.total}>{money(quote.total)}</span>
              </div>
              <p className="text-xs text-stone-500">Free shipping on orders over $50. Any sales tax is added at checkout.</p>
            </div>
          )}
          {!user && (
            <input data-testid={CART.emailInput} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email for your receipt (optional)" className="w-full px-4 py-2.5 rounded-full border border-[#E7E0D3] focus:outline-none focus:ring-2 focus:ring-[#E06D44]/40" />
          )}
          <p data-testid={CART.emailNote} className="text-xs text-stone-500 -mt-2">If you don't finish checking out, we'll email you one reminder with your saved cart.</p>
          <button ref={checkoutRef} data-testid={CART.checkout} disabled={busy || !quote} onClick={checkout} className="w-full bg-[#E06D44] text-white py-4 rounded-full font-medium hover:bg-[#C95B33] disabled:opacity-60 transition-colors">
            {busy ? "Taking you to secure checkout…" : "Check out"}
          </button>
        </aside>
      </div>
      <MobileStickyBar show={!checkoutInView && !!quote} testId={CART.stickyBar} label={`${items.reduce((n, i) => n + i.qty, 0)} item(s) · ${quote?.shipping ? "plus shipping" : "free shipping"}`} value={quote ? money(quote.total) : "…"} action={busy ? "One moment…" : "Check out"} onAction={checkout} disabled={busy || !quote} />
    </main>
  );
}
